import React from 'react';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { LectureLecturer } from '../../../../../api/types/lecturers/lectures/lecture-lecturer';
import dateFormat from '../../../../../utils/date-format';
import { ProfessorLectureDb } from '../../../../../api/types/calendar-report/calendar-report-professor.type';
import { toast } from 'react-toastify';

interface MyLectureColumnsProps {
  onEditLectureName: (lecture: LectureLecturer) => void;
  allowEditName?: boolean;
}

// Template for displaying date
const dateTemplate = (rowData: LectureLecturer) => {
  return dateFormat.displayFormatDate(new Date(rowData.startTime));
};

// Template for displaying time range
const timeTemplate = (rowData: LectureLecturer) => {
  return dateFormat.formatTimeRangeBetweenDates(
    new Date(rowData.startTime),
    new Date(rowData.endTime),
  );
};

// Template for displaying program info
const programTemplate = (rowData: ProfessorLectureDb) => {
  return rowData.additionalProgramName
    ? `${rowData.programName} (${rowData.additionalProgramName})`
    : rowData.programName;
};

// Template for lecture name with edit button
const createLectureNameTemplate = (
  onEditLectureName: (lecture: LectureLecturer) => void,
  allowEdit: boolean,
) => {
  return (rowData: LectureLecturer) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{rowData.name}</span>
        <Button
          icon="pi pi-pencil"
          size="small"
          severity={allowEdit ? 'warning' : 'secondary'}
          text
          tooltipOptions={{ showOnDisabled: true }}
          tooltip={
            allowEdit ? 'עריכת שם המפגש' : 'לשינוי שם ההרצאה יש לפנות למנהלה'
          }
          disabled={!allowEdit}
          onClick={() => allowEdit && onEditLectureName(rowData)}
          style={{ width: '28px', height: '28px' }}
        />
      </div>
    );
  };
};

// Get common columns for all tables
export const getCommonColumns = (
  props: MyLectureColumnsProps,
  includeDate = true,
) => {
  const allowEditName = props.allowEditName !== false; // Default to true
  const lectureNameTemplate = createLectureNameTemplate(
    props.onEditLectureName,
    allowEditName,
  );

  return [
    <Column
      key="name"
      field="name"
      header="שם ההרצאה"
      body={lectureNameTemplate}
      style={{ textAlign: 'right', minWidth: '200px' }}
      headerStyle={{ textAlign: 'right' }}
    />,
    <Column
      key="courseName"
      field="courseName"
      header="קורס"
      style={{ textAlign: 'right', minWidth: '150px' }}
      headerStyle={{ textAlign: 'right' }}
    />,
    <Column
      key="courseName"
      field="additionalCourseName"
      header="שם קורס נוסף"
      style={{ textAlign: 'right', minWidth: '150px' }}
      headerStyle={{ textAlign: 'right' }}
    />,
    <Column
      key="program"
      field="program"
      header="תוכנית לימודים"
      body={programTemplate}
      style={{ textAlign: 'right', minWidth: '200px' }}
      headerStyle={{ textAlign: 'right' }}
    />,
    <Column
      /*key="branch"*/
      field="branch"
      header="שלוחה"
      style={{ textAlign: 'right', minWidth: '120px' }}
      headerStyle={{ textAlign: 'right' }}
    />,
    <Column
      /*key="classroom"*/
      field="classroom"
      header="כיתה"
      style={{ textAlign: 'right', minWidth: '100px' }}
      headerStyle={{ textAlign: 'right' }}
    />,
    ...(includeDate
      ? [
          <Column
            key="date"
            field="date"
            header="תאריך"
            body={dateTemplate}
            style={{ textAlign: 'right', minWidth: '120px' }}
            headerStyle={{ textAlign: 'right' }}
          />,
        ]
      : []),
    <Column
      key="time"
      field="time"
      header="שעות מתוכננות"
      body={timeTemplate}
      style={{ textAlign: 'right', minWidth: '120px' }}
      headerStyle={{ textAlign: 'right' }}
    />,
  ];
};
